// NotificationPrompt3.tsx
import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Slide,
  Typography,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';

type Phase = 'idle' | 'prompt' | 'loading' | 'granted' | 'denied' | 'unsubscribing';

interface NotificationPromptProps {
  onSubscribe: () => Promise<void>;
  onUnsubscribe?: () => Promise<void>;
  // triggerLabel?: string;
}

// Получить активную подписку из SW
async function getActiveSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

export default function NotificationPrompt({
  onSubscribe,
  onUnsubscribe,
  // triggerLabel = 'Увімкнути сповіщення webpush',
}: NotificationPromptProps) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [slideIn, setSlideIn] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { t } = useTranslation();

  // При монтировании — проверяем реальную подписку в SW
  useEffect(() => {
    if (Notification.permission === 'denied') {
      setPhase('denied');
      return;
    }
    getActiveSubscription().then((sub) => {
      if (sub) {
        setIsSubscribed(true);
        setPhase('granted');
      }
    });
  }, []);

  const show = () => {
    setPhase('prompt');
    setTimeout(() => setSlideIn(true), 10);
  };

  const hide = (callback?: () => void) => {
    setSlideIn(false);
    setTimeout(() => {
      callback?.();
    }, 350);
  };

  const handleAllow = async () => {
    setPhase('loading');
    try {
      await onSubscribe();
      setIsSubscribed(true);
      setPhase('granted');
      setSlideIn(true);
      // Через 2 сек схлопываем баннер, но фаза остаётся 'granted'
      setTimeout(() => setSlideIn(false), 2000);
    } catch {
      hide(() => setPhase('idle'));
    }
  };

  const handleDeny = () => hide(() => setPhase('idle'));

  // Отписка: сначала отписываемся в браузере, потом на сервере
  const handleUnsubscribe = async () => {
    setPhase('unsubscribing');
    try {
      const sub = await getActiveSubscription();
      if (sub) {
        await sub.unsubscribe(); // браузерная отписка
      }
      await onUnsubscribe?.(); // удаляем endpoint с сервера
      setIsSubscribed(false);
      setPhase('idle');
    } catch (err) {
      console.error('Unsubscribe failed', err);
      setPhase('granted'); // откат
    }
  };

  return (
    <>
      {/* Кнопка подписки */}
      {phase === 'idle' && !isSubscribed && (
        <Button 
          variant="contained" 
          color='success'
          onClick={show} 
          startIcon={<NotificationsIcon />}
        >
          {t('NotificationPrompt.Enable_webpush_notifications')}
        </Button>
      )}

      {/* Кнопка отписки */}
      {phase === 'granted' && isSubscribed && (
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<NotificationsOffIcon />}
          onClick={handleUnsubscribe}
        >
          {/* Вимкнути сповіщення webpush */}
          {t('NotificationPrompt.Disable_webpush_notifications')}
        </Button>
      )}

      {/* Идёт отписка */}
      {phase === 'unsubscribing' && (
        <Button variant="outlined" disabled startIcon={<CircularProgress size={16} />}>
          Вимкнення...
        </Button>
      )}

      {phase === 'denied' && (
        // <Typography variant="body2" color="text.secondary">
        //   Сповіщення заблоковані в налаштуваннях браузера
        // </Typography>
        <div className="alert alert-warning">
        <p>⚠️ <strong>Сповіщення заблоковані в налаштуваннях браузера.</strong></p>
        <p>Чтобы включить их:</p>
        <ol>
          <li>Нажмите на 🔒 <strong>значок замка</strong> слева от URL-адреса.</li>
          <li>Разрешите пункт <strong>Уведомления</strong>.</li>
          <li>Обновите страницу.</li>
        </ol>
      </div>
      )}

      {/* Sliding panel */}
      {(phase === 'prompt' || phase === 'loading' || phase === 'granted') && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            p: 3,
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        >
          <Slide in={slideIn} direction="up" timeout={400}>
            <Paper
              elevation={8}
              sx={{
                pointerEvents: 'all',
                width: '100%',
                maxWidth: 420,
                p: 3,
                borderRadius: 4,
              }}
            >
              {phase === 'granted' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckCircleOutlineIcon color="success" />
                  <Typography variant="body2" color="text.secondary">
                    Сповіщення увімкнено
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    <NotificationsNoneIcon fontSize="small" />
                  </Box>

                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Будьте в курсі подій
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                    Ми надсилаємо сповіщення лише про важливі події — без спаму.
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="inherit"
                      onClick={handleDeny}
                      disabled={phase === 'loading'}
                    >
                      Не зараз
                    </Button>

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleAllow}
                      disabled={phase === 'loading'}
                      startIcon={
                        phase === 'loading' ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : undefined
                      }
                    >
                      {phase === 'loading' ? 'Зачекайте...' : 'Дозволити'}
                    </Button>
                  </Box>
                </>
              )}
            </Paper>
          </Slide>
        </Box>
      )}
    </>
  );
}